"use client";

import { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
  Fade,
  CircularProgress,
  Grid,
} from "@mui/material";
import {
  CloudUpload,
  ContentCopy,
  Delete,
  Download,
  ExpandMore,
  CheckCircle,
  Code,
  Tune,
} from "@mui/icons-material";

function byteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(2)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

type CssMinifyOptions = {
  removeComments: boolean;
  collapseWhitespace: boolean;
  keepLicenseComments: boolean;
};

function stripCssComments(css: string, keepLicenseComments: boolean): string {
  let out = "";
  let i = 0;
  let inString: "'" | '"' | null = null;

  while (i < css.length) {
    const ch = css[i];

    if (inString) {
      out += ch;
      if (ch === "\\") {
        if (i + 1 < css.length) {
          out += css[i + 1];
          i += 2;
          continue;
        }
      } else if (ch === inString) {
        inString = null;
      }
      i += 1;
      continue;
    }

    if (ch === "'" || ch === '"') {
      inString = ch;
      out += ch;
      i += 1;
      continue;
    }

    if (ch === "/" && css[i + 1] === "*") {
      const isLicense = css[i + 2] === "!";
      const end = css.indexOf("*/", i + 2);
      if (end === -1) break;
      if (keepLicenseComments && isLicense) {
        out += css.slice(i, end + 2);
      }
      i = end + 2;
      continue;
    }

    out += ch;
    i += 1;
  }

  return out;
}

function minifyCss(css: string, options: CssMinifyOptions): string {
  let working = css;

  if (options.removeComments) {
    working = stripCssComments(working, options.keepLicenseComments);
  }

  let out = "";
  let i = 0;
  let inString: "'" | '"' | null = null;
  let pendingSpace = false;

  while (i < working.length) {
    const ch = working[i];

    if (inString) {
      out += ch;
      if (ch === "\\") {
        if (i + 1 < working.length) {
          out += working[i + 1];
          i += 2;
          continue;
        }
      } else if (ch === inString) {
        inString = null;
      }
      i += 1;
      continue;
    }

    if (ch === "'" || ch === '"') {
      if (pendingSpace && out && out[out.length - 1] !== " ") {
        out += " ";
      }
      pendingSpace = false;
      inString = ch;
      out += ch;
      i += 1;
      continue;
    }

    if (options.collapseWhitespace && /\s/.test(ch)) {
      pendingSpace = true;
      i += 1;
      continue;
    }

    const isPunct =
      ch === "{" || ch === "}" || ch === ":" || ch === ";" || ch === ",";

    if (isPunct) {
      // Remove whitespace before punctuation
      if (out.endsWith(" ")) out = out.slice(0, -1);
      // Remove trailing semicolon before }
      if (ch === "}" && out.endsWith(";")) out = out.slice(0, -1);
      out += ch;
      pendingSpace = false; // no whitespace after punctuation
      i += 1;
      continue;
    }

    if (pendingSpace) {
      if (out && out[out.length - 1] !== " ") {
        out += " ";
      }
      pendingSpace = false;
    }

    out += ch;
    i += 1;
  }

  return out.trim();
}

const SAMPLE_CSS = `/* Example: Minify this CSS */
body {
  font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  background: #f7f7fb;
}

.card {
  padding: 16px;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 12px;
}
`;

export default function CssMinifierConverter() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [minifying, setMinifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const [removeComments, setRemoveComments] = useState(true);
  const [collapseWhitespace, setCollapseWhitespace] = useState(true);
  const [keepLicenseComments, setKeepLicenseComments] = useState(true);

  const inputBytes = useMemo(() => byteLength(input), [input]);
  const outputBytes = useMemo(() => byteLength(output), [output]);
  const savingsPct = useMemo(() => {
    if (!output || inputBytes === 0) return null;
    const pct = ((inputBytes - outputBytes) / inputBytes) * 100;
    if (!Number.isFinite(pct)) return null;
    return Math.max(0, Math.min(100, pct));
  }, [inputBytes, outputBytes, output]);

  const handleMinify = async () => {
    setError(null);
    setCopied(false);

    const css = input.trim();
    if (!css) {
      setError("Please paste your CSS code to minify.");
      setOutput("");
      return;
    }

    setMinifying(true);
    try {
      const result = minifyCss(css, {
        removeComments,
        collapseWhitespace,
        keepLicenseComments,
      });
      setOutput(result);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to minify CSS. Please check your input.";
      setError(message);
      setOutput("");
    } finally {
      setMinifying(false);
    }
  };

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Failed to copy to clipboard");
    }
  };

  const handleDownload = () => {
    if (!output) return;
    const blob = new Blob([output], { type: "text/css" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "styles.min.css";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    setInput("");
    setOutput("");
    setError(null);
    setCopied(false);
  };

  const handleLoadSample = () => {
    setInput(SAMPLE_CSS);
    setOutput("");
    setError(null);
    setCopied(false);
  };

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (file: File | null) => {
    if (!file) return;
    try {
      const text = await file.text();
      setInput(text);
      setOutput("");
      setError(null);
      setCopied(false);
    } catch {
      setError("Failed to read the selected file.");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            CSS input
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <input
              ref={fileInputRef}
              type="file"
              accept=".css,text/css"
              style={{ display: "none" }}
              onChange={(e) => handleFileSelected(e.target.files?.[0] ?? null)}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={handlePickFile}
            >
              Upload .css
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Code />}
              onClick={handleLoadSample}
            >
              Load sample
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Tune />}
              onClick={() => setShowOptions((v) => !v)}
              endIcon={
                <ExpandMore
                  sx={{
                    transition: "transform 0.2s ease",
                    transform: showOptions ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              }
            >
              Options
            </Button>
          </Stack>
        </Stack>

        <TextField
          multiline
          rows={10}
          fullWidth
          placeholder={`Paste CSS here...\n\nTip: You can also upload a .css file.`}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
            if (output) setOutput("");
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: "monospace",
              fontSize: "0.9rem",
            },
          }}
        />

        <Collapse in={showOptions}>
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Minification options
            </Typography>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={removeComments}
                      onChange={(e) => setRemoveComments(e.target.checked)}
                    />
                  }
                  label="Remove comments"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepLicenseComments}
                      onChange={(e) => setKeepLicenseComments(e.target.checked)}
                      disabled={!removeComments}
                    />
                  }
                  label="Keep license comments (/*! ... */)"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={collapseWhitespace}
                      onChange={(e) => setCollapseWhitespace(e.target.checked)}
                    />
                  }
                  label="Collapse whitespace"
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 3 }}
          flexWrap="wrap"
        >
          <Button
            variant="contained"
            size="large"
            startIcon={
              minifying ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                <Code sx={{ color: "#ffffff" }} />
              )
            }
            onClick={handleMinify}
            disabled={!input.trim() || minifying}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: "1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {minifying ? "Minifying..." : "Minify CSS"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Delete />}
            onClick={handleClear}
          >
            Clear
          </Button>
        </Stack>

        {output && (
          <Fade in={!!output}>
            <Box sx={{ mt: 4 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                sx={{ mb: 2 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700}>
                    Minified output
                  </Typography>
                  <Chip
                    size="small"
                    label={`Input: ${formatBytes(inputBytes)}`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`Output: ${formatBytes(outputBytes)}`}
                    color="success"
                    variant="outlined"
                  />
                  {savingsPct !== null && (
                    <Chip
                      size="small"
                      label={`Saved: ${savingsPct.toFixed(1)}%`}
                      color="success"
                    />
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                    onClick={handleCopy}
                    variant="outlined"
                    color={copied ? "success" : "primary"}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={handleDownload}
                    variant="outlined"
                  >
                    Download .min.css
                  </Button>
                </Stack>
              </Stack>

              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  bgcolor: "#1e1e1e",
                  borderRadius: 2,
                  maxHeight: 320,
                  overflow: "auto",
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                    color: "#d4d4d4",
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {output}
                </Typography>
              </Paper>
            </Box>
          </Fade>
        )}
      </Paper>
    </Box>
  );
}
